import { Container, Link, Paper, Typography } from "@mui/material";
import { NavigationAndFooterLayout } from "../layouts";
import { NextPageWithLayout } from "@cm-websites/common";
import { NextSeo } from "next-seo";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import i18nConfig from "../../next-i18next.config";
import React from "react";
import type { GetStaticProps } from "next";

export const AgbPage: NextPageWithLayout = () => (
  <React.Fragment>
    <NextSeo title="Terms and Conditions | Culinario Mortale®" noindex />
    <Container
      maxWidth="md"
      sx={{ pt: { xs: 12, sm: 14, lg: 16 }, pb: { xs: 8, sm: 10, lg: 14, xl: 16 } }}
    >
      <Typography variant="h1" sx={{ pb: 6 }}>
        Terms and Conditions
      </Typography>

      <Typography variant="h2" sx={{ pt: 4, pb: 3 }}>
        1. Scope of application
      </Typography>
      <Typography variant="body1" paragraph>
        The following General Terms and Conditions apply to orders placed via our online shop
        www.culinario-mortale.com (hereinafter referred to as “supplier”).
      </Typography>
      <Typography variant="body1" paragraph>
        The version of the GTC valid at the time of the order shall be authoritative for the orders. The
        contractual language is English.
      </Typography>
      <Typography variant="body1" paragraph>
        Our online shop is aimed exclusively at consumers.
      </Typography>
      <Typography variant="body1" paragraph>
        Consumer is any natural person who enters into a legal transaction for purposes which are
        predominantly neither commercial nor self-employed. An entrepreneur is a natural or legal person,
        or a partnership with legal capacity, who acts in the exercise of his or her commercial activity
        when concluding a legal transaction.
      </Typography>
      <Typography variant="body1" paragraph>
        Via our online shop, Murder Mysteries can be purchased both as a printed game version (“physical
        goods”) and as a one-time online version (“digital service”). The one-time online version is made
        available to you by transmission of an access code for the duration of one game session.
      </Typography>

      <Typography variant="h2" sx={{ pt: 4, pb: 3 }}>
        2. Supplier
      </Typography>
      <Typography variant="body1" paragraph>
        In the event of the conclusion of a contract, a contract is concluded with
      </Typography>
      <Typography variant="body1" paragraph>
        Bischoff, Kauffmann & Otani GbR
        <br />
        Hohenzollerndamm 152
        <br />
        14199 Berlin
        <br />
        Germany
      </Typography>
      <Typography variant="body1" paragraph>
        represented by the managing partners Florian Bischoff, Jens Kauffmann, Takeshi Otani
      </Typography>
      <Typography variant="body1" paragraph>
        Phone.: +49 (0) 30 25 29 21 51
        <br />
        Email: support@culinario-mortale.com
        <br />
        Website: www.culinario-mortale.com
        <br />
        VAT identification number: DE288168520
      </Typography>
      <Typography variant="body1" paragraph>
        (hereinafter also referred to as <em>“supplier”</em>).
      </Typography>

      <Typography variant="h2" sx={{ pt: 4, pb: 3 }}>
        3. Conclusion of the contract
      </Typography>
      <Typography variant="body1" paragraph>
        The presentation of the products in our online shop does not represent a legally binding offer on
        our part, but rather a non-binding online catalogue of our products, combined with the likewise
        non-binding invitation to you to submit a binding offer.
      </Typography>
      <ul>
        <Typography variant="body1" paragraph component="li">
          The submission of a binding offer by you takes place after you have successfully completed the
          following order steps: Selection of the desired item
        </Typography>

        <Typography variant="body1" paragraph component="li">
          Non-binding addition of the desired product to the shopping basket by pressing the “Add to
          basket” button.
        </Typography>

        <Typography variant="body1" paragraph component="li">
          Checking the items in the shopping cart
        </Typography>

        <Typography variant="body1" paragraph component="li">
          Pressing the button “Proceed to checkout”
        </Typography>

        <Typography variant="body1" paragraph component="li">
          Entering the billing and shipping address and selecting the desired payment method
        </Typography>

        <Typography variant="body1" paragraph component="li">
          Checking the order and all other entries made
        </Typography>

        <Typography variant="body1" paragraph component="li">
          Submission of a binding offer by pressing the button “Order with obligation to pay”.
        </Typography>
      </ul>

      <Typography variant="body1" paragraph>
        At any time during the ordering process, the customer may correct any entries made or cancel the
        ordering process by clicking on the shopping basket button or by clicking on the back button in
        his browser.
      </Typography>

      <Typography variant="body1" paragraph>
        The acceptance of the submitted binding offer by the supplier takes place via an automated order
        confirmation email.
      </Typography>

      <Typography variant="body1" paragraph>
        We save the text of the contract and send you the order data and our GTC by email.
      </Typography>

      <Typography variant="h2" sx={{ pt: 4, pb: 3 }}>
        4. Right of withdrawal
      </Typography>
      <Typography variant="body1" paragraph>
        Consumers have a right of withdrawal in accordance with the conditions set out below.
      </Typography>

      <Paper variant="outlined" sx={{ px: 6, py: 8 }}>
        <Typography variant="h3" sx={{ pt: 0, pb: 3 }}>
          Withdrawal instructions
        </Typography>

        <Typography variant="h4" sx={{ pt: 3, pb: 1 }}>
          RIGHT OF WITHDRAWAL
        </Typography>
        <Typography variant="body1" paragraph>
          You have the right to withdraw from this contract within 14 days without giving reason.
        </Typography>
        <Typography variant="body1" paragraph>
          The withdrawal period will expire 14 days from the day on which you or a third party named by
          you, who is not the carrier, has or has taken possession of the goods or from the day of the
          conclusion of the contract, in the case of digital content that is not delivered on a physical
          data carrier.
        </Typography>
        <Typography variant="body1" paragraph>
          In order to exercise your right of withdrawal, you must inform us (Culinario Mortale –
          Bischoff, Kauffmann & Otani GbR, Florian Bischoff, Hohenzollerndamm 152, 14199 Berlin, Phone.:
          +49 (0) 30 25 29 21 51, email: support@culinario-mortale.com) by means of a clear declaration
          (e.g. a letter sent by post, fax or email) of your decision to withdraw from this contract. You
          may use the enclosed model withdrawal form, but it is not obligatory.
        </Typography>

        <Typography variant="body1" paragraph>
          To meet the withdrawal deadline, it is sufficient that you send the notification of the
          exercise of the right of withdrawal before the expiry of the withdrawal period.
        </Typography>

        <Typography variant="h4" sx={{ pt: 3, pb: 1 }}>
          EFFECTS OF WITHDRAWAL
        </Typography>
        <Typography variant="body1" paragraph>
          If you withdraw from this contract, we shall reimburse to you all payments received from you,
          including the costs of delivery (with the exception of the supplementary costs resulting from
          other than the least expensive type of standard delivery offered by us), without undue delay
          and in any event not later than 14 days from the day on which we are informed about your
          decision to withdraw from this contract. We will carry out such reimbursement using the same
          means of payment as you used for the initial transaction, unless you have expressly agreed
          otherwise; in any event, you will not incur any fees as result of such reimbursement.
        </Typography>
        <Typography variant="body1" paragraph>
          We may refuse reimbursement until we have received the goods back or until you have provided
          proof that you have returned the goods, whichever is the earlier.
        </Typography>
        <Typography variant="body1" paragraph>
          You must return or hand over the goods to us without delay and in any case no later than 14
          days from the day on which you notify us of the withdrawal from this contract. The deadline is
          met if you send the goods before the expiry of the period of 14 days.
        </Typography>
        <Typography variant="body1" paragraph>
          You shall bear the direct costs of returning the goods.
        </Typography>

        <Typography variant="h3" sx={{ pt: 3, pb: 0 }}>
          End of the withdrawal instructions
        </Typography>
      </Paper>

      <Typography variant="h3" sx={{ pt: 6, pb: 3 }}>
        Model withdrawal form
      </Typography>

      <Typography variant="body1" paragraph>
        If you wish to withdraw from the contract, please complete and return this form.
      </Typography>

      <Paper variant="outlined" sx={{ backgroundColor: "white", px: 6, py: 8 }}>
        <Typography variant="body1" paragraph>
          To:
          <br />
          Culinario Mortale
          <br />
          Bischoff, Kauffmann & Otani GbR
          <br />
          Hohenzollerndamm 152
          <br />
          14199 Berlin
          <br />
          Germany
          <br />
          E-Mail support@culinario-mortale.com
        </Typography>
        <Typography variant="body1" paragraph>
          I/We* hereby give notice that I/We* withdraw from my/our* contract of sale of the follo- wing
          goods/provision of the following service*:
          <br />
          _____________________________________________________
        </Typography>
        <Typography variant="body1" paragraph>
          Ordered on/received on*
          <br />
          __________________
        </Typography>
        <Typography variant="body1" paragraph>
          Name of consumer(s)
          <br />
          _____________________________________________________
        </Typography>
        <Typography variant="body1" paragraph>
          Address of consumer(s)
          <br />
          _____________________________________________________
        </Typography>
        <Typography variant="body1" paragraph>
          Signature of consumer(s) (only if this form is notified in writing)
          <br />
          __________________
        </Typography>
        <Typography variant="body1" paragraph>
          Date
          <br />
          __________________
        </Typography>
        <Typography variant="body1" paragraph>
          (*) Delete as appropriate
        </Typography>
      </Paper>

      <Typography variant="h2" sx={{ pt: 6, pb: 3 }}>
        5. Prices
      </Typography>
      <Typography variant="body1" paragraph>
        All prices include the statutory value added tax.
      </Typography>

      <Typography variant="h2" sx={{ pt: 4, pb: 3 }}>
        6. Payment methods
      </Typography>
      <Typography variant="body1" paragraph>
        The customer has the following payment options: PayPal, credit card. Further payment methods are
        not offered.
      </Typography>

      <Typography variant="h2" sx={{ pt: 4, pb: 3 }}>
        7. Delivery
      </Typography>
      <Typography variant="body1" paragraph>
        Unless clearly stated otherwise in the item description, all goods are ready for immediate
        dispatch. The delivery of the ordered goods within the European Union takes place within 10
        working days.
      </Typography>
      <Typography variant="body1" paragraph>
        The delivery of ordered digital contents (one-time online version) takes place by electronic
        transmission (by email) of an access code.
      </Typography>

      <Typography variant="h2" sx={{ pt: 4, pb: 3 }}>
        8. Retention of Title
      </Typography>
      <Typography variant="body1" paragraph>
        Until full payment of ordered goods by the consumer, the delivered goods remain the property of
        the supplier.
      </Typography>

      <Typography variant="h2" sx={{ pt: 4, pb: 3 }}>
        9. Warranty
      </Typography>
      <Typography variant="body1" paragraph>
        The legal warranty regulations apply.
      </Typography>

      <Typography variant="h2" sx={{ pt: 4, pb: 3 }}>
        10. Dispute resolution
      </Typography>
      <Typography variant="body1" paragraph>
        The European Commission provides a platform for online dispute resolution (OS). You can find this
        at <Link href="https://ec.europa.eu/consumers/odr">https://ec.europa.eu/consumers/odr</Link>.
      </Typography>
      <Typography variant="body1" paragraph>
        Our email address is{" "}
        <Link href="mailto:support@culinario-mortale.com">support@culinario-mortale.com</Link>.
      </Typography>
      <Typography variant="body1" paragraph>
        We are not obliged or willing to participate in a dispute resolution procedure before a consumer
        arbitration board.
      </Typography>

      <Typography variant="h2" sx={{ pt: 4, pb: 3 }}>
        11. Severability clause
      </Typography>
      <Typography variant="body1" paragraph>
        The invalidity of any provision of these General Terms and Conditions shall not affect the
        validity of the remaining provisions.
      </Typography>

      <Typography variant="h2" sx={{ pt: 4, pb: 3 }}>
        12. Amendment of the general terms and conditions
      </Typography>
      <Typography variant="body1" paragraph>
        We, as the responsible party, reserve the right to change our General Terms and Conditions at any
        time with regard to applicable laws and regulations. Current status is May 2023.
      </Typography>
    </Container>
  </React.Fragment>
);

AgbPage.getLayout = (page) => <NavigationAndFooterLayout>{page}</NavigationAndFooterLayout>;

export default AgbPage;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(locale
        ? await serverSideTranslations(locale, ["c-common", "c-user-consent-management"], i18nConfig)
        : {}),
    },
  };
};
